import { Fragment, useEffect, useState } from 'react';
import { useAppDispatch } from '../../../redux/HooksRedux';
import { OptionI, WithoutIncomeInterface } from '../../../../types/interface';
import * as withoutHouseholdAction from '../../../redux/SubsidyWithoutIncome/SubsidyWithoutIncomeAction';
import {
  errorNotification,
  successNotification
} from 'base/notifications/Notifications';
import {
  capitalizeFirstLetter,
  checkAccess,
  convertToLabelValue,
  formatDateOnly,
  parseIntoNumber
} from 'utils/helpers';
import validationFunctions from 'utils/validationUtils';
import { SubsidyWithoutIncomeValidationSet } from 'utils/validations/ValidationsSet/SubsudyWithoutIncomeValidationSet';
import { Card } from 'react-bootstrap';
import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import { Form } from 'react-bootstrap';
import { t } from 'i18next';
import { UilAngleRight } from '@iconscout/react-unicons';
import { StandardButton } from 'base/StandardButton/StandardButton';
import StandardSelect from 'base/StandardSelect/StandardSelect';
import { StandardCurrency } from 'base/StandardCurrency/StandardCurrency';
import { StandardLoader } from 'base/Loader/Loader';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import {
  EDUCATION,
  HEALTH,
  modules,
  SCHOOL_ACTIVITY_OF_CHILD
} from 'utils/enums/misc';
import {
  DYNAMIC_APPLICATION_EDUCATION_ID,
  DYNAMIC_APPLICATION_HEALTH_ID
} from 'utils/enums/dynamic_misc';

interface WithoutIncomeSubsicidyCalculationPropsI {
  disabled?: boolean;
  application_type?: string;
  net_income?: number;
  refund_percentage?: number;
  refundable_amount?: number;
}

export const WithoutIncomeSubsidyCalculation = (
  props: WithoutIncomeSubsicidyCalculationPropsI
) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [showError, setShowError] = useState(false);
  const [totalAmount, setTotalAmount] = useState(520);
  const [apiCallFlag, setApiCallFlag] = useState(false);
  const [applicationTypeOption, setApplicationTypeOption] =
    useState<OptionI[]>();
  const [applicationType, setApplicationType] = useState(100104);
  const [supportPerChild, setSupportPerChild] = useState<number | null>(0);
  const [supportPerYear, setSupportPerYear] = useState<number | null>(0);

  const currentDate = new Date();
  const withoutIncomeData: WithoutIncomeInterface = {
    support_provided: null,
    configuration_threshold_invoice: null,
    created_at: currentDate.toDateString(),
    updated_at: currentDate.toDateString(),
    maximum_support_provided_per_child: null,
    maximum_support_provided_per_user_per_year: null
  };
  const [formData, setFormData] = useState<WithoutIncomeInterface[]>([
    withoutIncomeData
  ]);
  const userPermission = useSelector(
    (state: RootState) => state.userProfile.permission
  );

  const handleChange = (
    index: number,
    key: keyof WithoutIncomeInterface,
    value: WithoutIncomeInterface[keyof WithoutIncomeInterface]
  ) => {
    setFormData(prevFormData => {
      const updatedFormData = [...prevFormData];
      updatedFormData[index] = {
        ...updatedFormData[index],
        [key]: value ? parseFloat(value as string) : null
      };
      return updatedFormData;
    });
  };

  const getAllWithoutHouseholdCalculation = async (
    application_type: number
  ) => {
    setIsLoading(true);
    try {
      const response = await dispatch(
        withoutHouseholdAction.getAllWithoutIncomeConfiguration(
          Number(application_type)
        )
      );
      if (response?.status === 200) {
        if (response?.data?.length == 0) {
          setApiCallFlag(false);
          setFormData([withoutIncomeData]);
          setTotalAmount(520);
        } else {
          setApiCallFlag(true);
          setFormData(response?.data?.data);
          setTotalAmount(response?.data?.data[0].maximum_support_provided);
          setSupportPerChild(
            response.data?.data[0].maximum_support_provided_per_child
          );
          setSupportPerYear(
            response.data?.data[0].maximum_support_provided_per_user_per_year
          );
        }
        setIsLoading(false);
      } else {
        errorNotification(response.data?.message);
        setIsLoading(false);
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  const getAllApplicationTypeRequest = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(
        withoutHouseholdAction.getAllApplicationTypeOption()
      );

      if (response.status === 200) {
        const convertValue = convertToLabelValue(
          response?.data?.data,
          'name',
          'application_request_id'
        );
        setApplicationTypeOption(convertValue.filter(a => a.value !== DYNAMIC_APPLICATION_EDUCATION_ID));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    setIsLoading(true);
    e.preventDefault();
    const apiData = {
      configurations: [
        {
          ...formData[0],
          maximum_support_provided: totalAmount,
          maximum_support_provided_per_child: supportPerChild,
          maximum_support_provided_per_user_per_year: supportPerYear
        }
      ]
    };
    let validation = {};
    formData.map(item => {
      const { support_provided, configuration_threshold_invoice } = item;
      validation = {
        ...validation,
        support_provided,
        configuration_threshold_invoice
      };
    });

    const validate = validationFunctions.checkFormValidity(
      validation,
      SubsidyWithoutIncomeValidationSet
    );
    if (!validate) {
      setShowError(true);
      setIsLoading(false);
      return;
    } else {
      setShowError(false);
      if (apiCallFlag) {
        try {
          const response = await dispatch(
            withoutHouseholdAction.updateWithoutIncomeConfiguration(
              Number(applicationType),
              apiData
            )
          );

          if (response?.status === 200) {
            successNotification(response.data?.message);
            getAllWithoutHouseholdCalculation(applicationType);
          } else {
            errorNotification(response?.data?.message);
          }
        } catch (error) {
          console.log(error);
        } finally {
          setIsLoading(false);
        }
      } else {
        try {
          const response = await dispatch(
            withoutHouseholdAction.createWithoutIncomeConfiguration(
              Number(applicationType),
              apiData
            )
          );

          if (response?.status === 201) {
            successNotification(response.data?.message);
            getAllWithoutHouseholdCalculation(applicationType);
          } else {
            errorNotification(response?.data?.message);
          }
        } catch (error) {
          console.log(error);
        } finally {
          setIsLoading(false);
        }
      }
    }
  };

  const handleApplicationTypeChange = (value: number) => {
    setApplicationType(value);
    getAllWithoutHouseholdCalculation(value);
  };

  useEffect(() => {
    setIsLoading(true);
    if (props.application_type) {
      if (props.application_type === HEALTH) {
        getAllWithoutHouseholdCalculation(
          DYNAMIC_APPLICATION_HEALTH_ID as number
        );
      } else if (
        props.application_type.toLowerCase() ===
        SCHOOL_ACTIVITY_OF_CHILD.toLowerCase()
      ) {
        getAllWithoutHouseholdCalculation(
          DYNAMIC_APPLICATION_EDUCATION_ID as number
        );
      }
    } else {
      getAllApplicationTypeRequest();
      getAllWithoutHouseholdCalculation(applicationType);
    }
  }, [props.application_type]);

  return (
    <Card>
      <ConditionalRender condition={!isLoading} fallBack={<StandardLoader />}>
        <Form onSubmit={handleSubmit}>
          <div
            className="m-3 p-3 border-radius-15 border-radius-10 has-box-shadow"
            id="event_module_id"
          >
            <ConditionalRender condition={!props.disabled}>
              <div className="row pb-2">
                <div className="col-lg-12 d-flex justify-content-between align-items-center mx-1 padding-bottom-20 padding-top-6">
                  <h4 className="mb-0 fs-20 font-semi-bold">
                    {capitalizeFirstLetter(t('header.text.text_11'))}
                  </h4>
                  <div className="d-flex justify-content-end align-items-center">
                    <div
                      id="search_input"
                      className="position-relative h-40 w-300 mx-2"
                    >
                      <StandardSelect
                        placeholder={'Select Application Type'}
                        value={applicationType}
                        onChange={e => handleApplicationTypeChange(e)}
                        className={'mb-0'}
                        options={applicationTypeOption as OptionI[]}
                        id={''}
                        label={''}
                        formClassName={'w-330'}
                        showError={false}
                        validations={[]}
                        disabled={props.disabled}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </ConditionalRender>
            {/* <div className="row mb-3">
              <ConditionalRender
                condition={applicationType !== DYNAMIC_APPLICATION_HEALTH_ID}
              >
                <div className="col-lg-6 col-12">
                  <StandardCurrency
                    value={supportPerChild}
                    placeholder={t('general.text_13')}
                    onChange={e => {
                      if (e) {
                        setSupportPerChild(parseFloat(e?.toString()));
                      } else {
                        setSupportPerChild(null);
                      }
                    }}
                    showError={false}
                    className={''}
                    label={capitalizeFirstLetter(t('general.text_13'))}
                    validations={[]}
                  />
                </div>
              </ConditionalRender>
              <div className="col-lg-6 col-12">
                <StandardCurrency
                  value={supportPerYear}
                  placeholder={t('general.text_14')}
                  onChange={e => {
                    if (e) {
                      setSupportPerYear(parseFloat(e?.toString()));
                    } else {
                      setSupportPerYear(null);
                    }
                  }}
                  showError={false}
                  className={''}
                  label={capitalizeFirstLetter(t('general.text_14'))}
                  validations={[]}
                />
              </div>
            </div> */}
            {formData.map((item, index) => {
              return (
                <Fragment key={`without-income-subcidy-config-${index}`}>
                  <div className="row mb-2">
                    <div className="col-lg-2"></div>
                    <div className="col-lg-3">
                      <h5 className="mb-0 font-medium me-4">
                        {t('subsidy_calculation_without.placeholder.text_1')}{' '}
                        (&euro;)
                      </h5>
                    </div>
                    <div className="col-1"></div>
                    <div className="col-lg-3 d-flex justify-content-start">
                      <h5 className="mb-0 font-medium me-4">
                        {t('subsidy_calculation_without.placeholder.text_2')}{' '}
                        (&euro;)
                      </h5>
                    </div>
                    <div className="col-lg-3 d-flex justify-content-start">
                      <h5 className="mb-0 font-medium me-4">
                        {t('subsidy_calculation_without.general.text_3')}{' '}
                        (&euro;)
                      </h5>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-lg-2 d-flex justify-content-center align-items-center h-40">
                      <h5 className="mb-1 font-semi-bold me-4">
                        {t('subsidy_calculation_without.condition')}
                      </h5>
                      <UilAngleRight />
                    </div>

                    <div className="col-lg-3">
                      <StandardCurrency
                        value={item?.configuration_threshold_invoice as number}
                        placeholder={t(
                          'subsidy_calculation_without.placeholder.text_1'
                        )}
                        onChange={e => {
                          handleChange(
                            index,
                            'configuration_threshold_invoice',
                            parseIntoNumber(e)
                          );
                        }}
                        showError={showError}
                        className={''}
                        validations={
                          SubsidyWithoutIncomeValidationSet.configuration_threshold_invoice
                        }
                        label=""
                        disabled={props.disabled}
                      />
                    </div>
                    <div className="col-lg-1">
                      <h5 className="h-40 mb-0 font-semi-bold d-flex justify-content-center align-items-center">
                        {t('subsidy_calculation_with.general.text_3')}
                      </h5>
                    </div>
                    <div className="col-lg-3">
                      <StandardCurrency
                        value={item?.support_provided as number}
                        placeholder={t(
                          'subsidy_calculation_without.placeholder.text_2'
                        )}
                        onChange={e => {
                          handleChange(
                            index,
                            'support_provided',
                            parseIntoNumber(e)
                          );
                        }}
                        showError={showError}
                        className={''}
                        validations={
                          SubsidyWithoutIncomeValidationSet.support_provided
                        }
                        label={''}
                        disabled={props.disabled}
                      />
                    </div>
                    <div className="col-lg-3">
                      <StandardCurrency
                        value={totalAmount}
                        placeholder={t(
                          'subsidy_calculation_without.placeholder.text_3'
                        )}
                        onChange={e => setTotalAmount(e as number)}
                        showError={false}
                        className={''}
                        label=""
                        validations={[]}
                        disabled={props.disabled}
                      />
                    </div>
                  </div>
                  <div className="row mb-2">
                    <div className="col-lg-2 fs-16 font-semi-bold d-flex justify-content-center">
                      {t('subsidy_calculation_without.log.log_1')}
                    </div>
                    <div className="col-lg-3">
                      <p className="mb-0">
                        {formatDateOnly(item?.created_at as string)}
                      </p>
                    </div>
                    <div className="col-lg-1 fs-16 font-semi-bold d-flex justify-content-center">
                      {t('subsidy_calculation_without.log.log_2')}
                    </div>
                    <div className="col-lg-3">
                      <p className="mb-0 ms-2">
                        {formatDateOnly(item?.updated_at as string)}
                      </p>
                    </div>
                  </div>

                  <div className="row refund-eligibili border-top mt-1 pt-2">
                    <div className="col-lg-12 mx-1">
                        <h4 className=" py-1 font-semi-bold">
                          {t('subsidy_calculation_without.message.value_1')}
                        </h4>
                        <div className="ms-3">
                          <p>
                            {t('subsidy_calculation_with.message.value_2')}{' '}
                            <strong>
                              {t('subsidy_calculation_without.message.value_3')}
                            </strong>{' '}
                            {t('subsidy_calculation_without.message.value_4')}{' '}
                            <strong>
                              {' '}
                              {t('subsidy_calculation_without.message.value_5')}
                            </strong>{' '}
                            {t('subsidy_calculation_without.message.value_6')}{' '}
                            <strong>
                              {' '}
                              {t(
                                'subsidy_calculation_without.message.value_7'
                              )}{' '}
                              {item?.support_provided}{' '}
                              {t('subsidy_calculation_without.message.value_8')}{' '}
                            </strong>{' '}
                            {t('subsidy_calculation_without.message.value_9')}{' '}
                            <strong>
                              {t(
                                'subsidy_calculation_without.message.value_10'
                              )}{' '}
                              {totalAmount}{' '}
                              {t('subsidy_calculation_without.message.value_8')}
                            </strong>{' '}
                            {t('subsidy_calculation_without.message.value_12')}
                          </p>
                        </div>
                        <h4 className="py-1 font-semi-bold">
                          {t('subsidy_calculation_without.message.value_13')}
                        </h4>
                        <div className="ms-3">
                          <p>
                            {t('subsidy_calculation_with.message.value_2')}{' '}
                            <strong>
                              {t(
                                'subsidy_calculation_without.message.value_15'
                              )}{' '}
                              {'590'}{' '}
                              {t('subsidy_calculation_without.message.value_8')}{' '}
                            </strong>
                            ,{t('subsidy_calculation_without.message.value_17')}{' '}
                            <strong>
                              {t(
                                'subsidy_calculation_without.message.value_18'
                              )}{' '}
                              {item?.configuration_threshold_invoice}{' '}
                              {t('subsidy_calculation_without.message.value_8')}
                            </strong>
                            {t('subsidy_calculation_without.message.value_20')}{' '}
                            <strong>
                              {item?.support_provided}{' '}
                              {t('subsidy_calculation_without.message.value_8')}{' '}
                            </strong>
                            {t('subsidy_calculation_without.message.value_22')}
                          </p>
                          <ul>
                            <li>
                              {totalAmount} - {item?.support_provided} ={' '}
                              <strong>
                                450{' '}
                                {t(
                                  'subsidy_calculation_without.message.value_8'
                                )}
                              </strong>{' '}
                              {t(
                                'subsidy_calculation_without.message.value_24'
                              )}
                            </li>
                            <li>
                              450 - 70 ={' '}
                              <strong>
                                380{' '}
                                {t(
                                  'subsidy_calculation_without.message.value_8'
                                )}
                              </strong>{' '}
                              {t(
                                'subsidy_calculation_without.message.value_24'
                              )}
                            </li>
                          </ul>
                          <p>
                            {t('subsidy_calculation_without.message.value_27')}{' '}
                            <strong>
                              {t(
                                'subsidy_calculation_without.message.value_28'
                              )}{' '}
                              {item?.support_provided}{' '}
                              {t('subsidy_calculation_without.message.value_8')}
                            </strong>
                            {t('subsidy_calculation_without.message.value_30')}
                          </p>
                        </div>
                    </div>
                  </div>
                </Fragment>
              );
            })}
            <div className="row justify-content-center">
              <div className="col-lg-2">
                <ConditionalRender
                  condition={
                    checkAccess(
                      [
                        modules.CONFIGURATION.create,
                        modules.CONFIGURATION.edit
                      ],
                      userPermission
                    ) && !props.disabled
                  }
                >
                  <div className="table-button-style me-lg-4 me-md-1">
                    <StandardButton
                      onClick={() => {}}
                      type={'submit'}
                      buttonText={
                        !apiCallFlag
                          ? t('general.common_text.text_2')
                          : t('general.common_text.text_18')
                      }
                      className={'add-btn-style px-4'}
                    />
                  </div>
                </ConditionalRender>
              </div>
            </div>
          </div>
        </Form>
      </ConditionalRender>
    </Card>
  );
};
